import {
  IndexVariable,
  isIndexVariable,
  isSubRecordFieldSet,
  RecordGridFieldSet,
  RecordGridField,
  SubRecordGridFieldSet,
  SubRecordProps,
} from '.';
import { max } from 'lodash';
import * as oPath from 'Lib/Object/Path/Immutable';
import { joinPaths } from 'Lib/Object/Path/shared';
import { Button, ConfirmDialog, Label } from 'Components/Core';
import { MdAddCircle, MdRemoveCircle } from 'react-icons/md';
import { getRecordHeader, isCreatePrevented, isRemovePrevented } from './helpers';
import { hasNonEmptyValue } from 'Lib/Any';
import { ArrayModifierPayLoad } from '../WorksheetEditorInjectedPropsProvider/types';
import { omit } from 'ramda';

/**
 * Generates a flat iterable of {@link RecordGridField} for a given field set
 * that will become the final rows/columns of a grid
 *
 * Chiefly concerned with removing "hidden" fields and unwrapping sub-record field sets
 */
export function* generateFields(
  records: Array<any>,
  fieldSet: RecordGridFieldSet,
  name: string,
  addArrayEntry: (payload: ArrayModifierPayLoad) => void,
  removeArrayEntry: (payload: ArrayModifierPayLoad) => void
): Generator<RecordGridField, void, unknown> {
  for (let field of fieldSet) {
    // Drop hidden fields
    if (field.hidden) continue;

    // If this is not a sub-record field set return it as is
    if (!isSubRecordFieldSet(field)) {
      yield field;
      continue;
    }

    // Generate fields for a sub-record field set
    const subRecordData = records?.map((r, i) => {
      const subRecordProps =
        (isIndexVariable((field as SubRecordGridFieldSet).recordProps)
          ? ((field as SubRecordGridFieldSet).recordProps as IndexVariable<SubRecordProps>)?.(i)
          : ((field as SubRecordGridFieldSet).recordProps as SubRecordProps)) ?? {};
      const subRecordsPath = joinPaths(name, i.toString(), field.name);
      let subRecordCount = oPath.get(r, field.name)?.length ?? 0;
      if (subRecordCount <= 0 && subRecordProps.enforceAtLeastOneRecord) subRecordCount = 1;

      return {
        subRecordProps,
        subRecordsPath,
        subRecordCount,
      };
    });

    const maxSubRecordLength = max(subRecordData?.map((d) => d.subRecordCount));
    const showSubHeading = subRecordData?.some(
      (r) =>
        r.subRecordProps?.heading === 'number' ||
        Array(r.subRecordCount)
          .fill(undefined)
          .some((_, i) => hasNonEmptyValue(getRecordHeader(r.subRecordProps?.heading, i)))
    );

    // eslint-disable no-loop-func

    /**
     * Transforms a sub record component into a record component by returning
     * a function of the record index that will:
     *
     * * Remove components past the end of the sub-records
     * * Show subrecord control buttons at the end of sub-records (where we want this)
     */
    // We're getting a warning that namespace JSX could vary as it is defined outside the loop
    // Not concerned about this so we're suppressing the error, but if you make big changes to
    // this function it'll be worth enabling it to see if anything else has a warning
    // eslint-disable-next-line no-loop-func
    const getRecordComponentForSubRecordComponent = ({
      subRecordComponent,
      subRecordIndex,
      showButtonsAtEndOfSubRecords,
    }: {
      subRecordComponent: ({ subRecordProps }: { subRecordProps: SubRecordProps }) => JSX.Element;
      subRecordIndex: number;
      showButtonsAtEndOfSubRecords: boolean;
    }) => (recordIndex: number) => {
      const { subRecordCount, subRecordProps, subRecordsPath } = subRecordData?.[recordIndex];

      // If there are at least this many subrecords, show component
      if (subRecordCount > subRecordIndex) return subRecordComponent({ subRecordProps });

      // If this is the first component after the subrecords end, show buttons
      if (subRecordCount === subRecordIndex && showButtonsAtEndOfSubRecords) {
        const allowRemove = !isRemovePrevented(subRecordCount - 1, subRecordCount, subRecordProps);
        const allowCreate = !isCreatePrevented(subRecordCount, subRecordProps);
        const recordGroupingSize = subRecordProps.recordGroupingSize ?? 1;

        return (
          <>
            {allowRemove && (
              <ConfirmDialog {...subRecordProps?.removeConfirm}>
                <Button
                  subtle
                  error
                  onClick={() => {
                    [...Array(recordGroupingSize).keys()].forEach((k) =>
                      removeArrayEntry!({
                        name: subRecordsPath,
                        index: subRecordCount - 1 - k,
                        data: undefined,
                        sync: k === recordGroupingSize - 1,
                      })
                    );
                  }}
                >
                  <MdRemoveCircle size="1.5em" />
                </Button>
              </ConfirmDialog>
            )}
            {allowCreate && allowRemove && ' / '}
            {allowCreate && (
              <Button
                subtle
                success
                onClick={() => {
                  [...Array(recordGroupingSize).keys()].forEach((k) =>
                    addArrayEntry!({
                      name: subRecordsPath,
                      index: k + subRecordCount,
                      data: undefined,
                      sync: k === recordGroupingSize - 1,
                    })
                  );
                }}
              >
                <MdAddCircle size="1.5em" />
              </Button>
            )}
          </>
        );
      }

      // We are past the end of the sub-records, so show nothing
      return <></>;
    };

    for (let subRecordIndex = 0; subRecordIndex < maxSubRecordLength; subRecordIndex++) {
      // Subheading field
      if (showSubHeading)
        yield {
          name: joinPaths(field.name),
          label: '',
          component: getRecordComponentForSubRecordComponent({
            subRecordComponent: ({ subRecordProps }) => (
              <Label emph>{getRecordHeader(subRecordProps.heading, subRecordIndex)}</Label>
            ),
            subRecordIndex,
            showButtonsAtEndOfSubRecords: true,
          }),
        };
      // Fields in the subrecord field set
      for (let [subfieldIndex, subField] of field.fields.filter((f) => !f.hidden).entries()) {
        yield {
          fieldSubRecordIndex: subRecordIndex,
          name: joinPaths(field.name, subRecordIndex.toString(), subField.name),
          additionalMessagePaths: subField.additionalMessagePaths?.map((p) =>
            joinPaths(field.name, subRecordIndex.toString(), p)
          ),
          component: getRecordComponentForSubRecordComponent({
            subRecordComponent: (_) =>
              isIndexVariable(subField.component) ? subField.component(subRecordIndex) : subField.component,
            subRecordIndex,
            showButtonsAtEndOfSubRecords: subfieldIndex === 0 && !showSubHeading, // Show buttons aligned with first field if we don't show a heading
          }),
          ...omit(['name', 'additionalMessagePaths', 'component'], subField),
        };
      }
    }

    // Control buttons at end of all records
    if (
      subRecordData?.some(
        (r) =>
          r.subRecordCount === maxSubRecordLength &&
          (!isCreatePrevented(r.subRecordCount, r.subRecordProps) ||
            !isRemovePrevented(r.subRecordCount - 1, r.subRecordCount, r.subRecordProps))
      )
    )
      yield {
        name: '',
        label: '',
        component: getRecordComponentForSubRecordComponent({
          subRecordComponent: (_) => <></>,
          subRecordIndex: maxSubRecordLength,
          showButtonsAtEndOfSubRecords: true,
        }),
      };
  }
}
